﻿/**
 * InteractiveList â€” renders WhatsApp-style expandable list sections.
 */

import React, { useState, useMemo, useId } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, FlatList } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS, OPACITY } from '../../utils/design-tokens';
import { useBackHandler } from '../../navigation/useBackHandler';

interface ListItem {
  id: string;
  title: string;
  description?: string;
}

interface Section {
  title: string;
  rows: ListItem[];
}

interface Props {
  sections: Section[];
  buttonText?: string;
  onSelect: (itemId: string, itemLabel: string) => void;
}

export default function InteractiveList({ sections, buttonText, onSelect }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const modalId = useId();
  const [visible, setVisible] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const handleSelect = (itemId: string, itemLabel: string) => {
    setVisible(false);
    setSelectedLabel(itemLabel);
    onSelect(itemId, itemLabel);
  };

  useBackHandler({
    id: `interactive-list-${modalId}`,
    label: buttonText || t('common.all'),
    priority: 950,
    enabled: visible,
    onBack: () => {
      setVisible(false);
      return true;
    },
  });

  return (
    <View style={s.container}>
      <TouchableOpacity
        style={[s.triggerButton, selectedLabel && s.triggerDisabled]}
        onPress={() => !selectedLabel && setVisible(true)}
        activeOpacity={0.7}
        disabled={!!selectedLabel}
        accessibilityRole="button"
        accessibilityLabel={selectedLabel || buttonText || t('common.search')}
      >
        <Text style={[s.triggerText, selectedLabel && s.triggerTextDisabled]}>
          {selectedLabel ? t('chat.optionSelected', { label: selectedLabel }) : (buttonText || t('common.all'))}
        </Text>
        {!selectedLabel && <Text style={s.arrow}>â–¼</Text>}
      </TouchableOpacity>

      <Modal visible={visible} transparent animationType="slide" onRequestClose={() => setVisible(false)}>
        <View style={s.overlay}>
          <View style={s.sheet}>
            <View style={s.sheetHeader}>
              <Text style={s.sheetTitle}>{buttonText || t('common.all')}</Text>
              <TouchableOpacity onPress={() => setVisible(false)} accessibilityRole="button" accessibilityLabel={t('common.close')}>
                <Text style={s.closeButton}>âœ•</Text>
              </TouchableOpacity>
            </View>

            <FlatList
              data={sections}
              keyExtractor={(_, i) => `section-${i}`}
              renderItem={({ item: section }) => (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>{section.title}</Text>
                  {section.rows.map((row) => (
                    <TouchableOpacity
                      key={row.id}
                      style={s.listItem}
                      onPress={() => handleSelect(row.id, row.title)}
                      accessibilityRole="button"
                      accessibilityLabel={row.title}
                    >
                      <Text style={s.itemTitle}>{row.title}</Text>
                      {row.description ? (
                        <Text style={s.itemDesc}>{row.description}</Text>
                      ) : null}
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    marginTop: SPACING.sm,
  },
  triggerButton: {
    backgroundColor: colors.primaryLight,
    borderRadius: RADIUS.sm,
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.base,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
  },
  triggerText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.primary,
    marginRight: 6,
  },
  triggerDisabled: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  triggerTextDisabled: {
    color: '#fff',
  },
  arrow: {
    fontSize: FONT_SIZE.xs,
    color: colors.primary,
  },
  overlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: RADIUS.xl,
    borderTopRightRadius: RADIUS.xl,
    maxHeight: '70%',
    padding: SPACING.base,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: SPACING.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sheetTitle: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
  },
  closeButton: {
    fontSize: FONT_SIZE.md,
    color: colors.textSecondary,
    paddingHorizontal: SPACING.sm,
  },
  section: {
    marginTop: SPACING.md,
  },
  sectionTitle: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  listItem: {
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  itemTitle: {
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
    color: colors.text,
  },
  itemDesc: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
});
